import React, { ReactNode } from 'react';

import { IconVariant, Pressable, Text } from '@rbilabs/universal-components';
import { Icon } from '@rbilabs/universal-components';
import { SafeAreaView, StatusBar, StyleSheet, View } from 'react-native';

import { theme } from 'styles/configure-theme';
import { primitive } from 'styles/constants/primitives';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';

const StyledBackIcon = Icon.withConfig({
  color: theme.token('icon-header-contrast'),
  size: '5',
  ...hiddenAccessibilityPlatformProps,
});

export type HeaderProps = {
  title: string;
  closeIconVariant?: IconVariant;
  closeIconPosition?: 'left' | 'right';
  variant?: 'light' | 'dark' | 'transparent';
  children?: ReactNode;
  onCloseIconClick: () => void;
};

const colorSchemes = {
  dark: {
    text: primitive.$white,
    background: primitive.bk.$bbqBrown,
  },
  light: {
    text: primitive.bk.$bbqBrown,
    background: primitive.bk.$mayoEggWhite,
  },
  transparent: {
    text: primitive.bk.$bbqBrown,
    background: 'transparent',
  },
} as const;

export function Header({
  title,
  closeIconVariant,
  closeIconPosition,
  variant = 'dark',
  children,
  onCloseIconClick,
}: HeaderProps) {
  const colors = colorSchemes[variant] ?? colorSchemes.dark;
  const textAndIconColor = colors.text;
  const headerColor = colors.background;

  return (
    <>
      <StatusBar
        barStyle={variant === 'dark' ? 'light-content' : 'dark-content'}
        backgroundColor={headerColor}
        translucent
      />
      <SafeAreaView style={{ flex: 0, backgroundColor: headerColor }} />
      <View
        style={[
          styles.headerContainer,
          {
            backgroundColor: headerColor,
          },
        ]}
      >
        {children ? (
          children
        ) : (
          <Text
            accessibilityRole="header"
            accessibilityLevel={2}
            color={textAndIconColor}
            style={styles.headerText}
          >
            {title}
          </Text>
        )}
        {!children && (
          <View
            style={[
              styles.iconContainer,
              closeIconPosition === 'left'
                ? { left: primitive.spacing.$3 }
                : { right: primitive.spacing.$3 },
            ]}
          >
            <Pressable onPress={onCloseIconClick}>
              <StyledBackIcon variant={closeIconVariant || 'cancel'} color={textAndIconColor} />
            </Pressable>
          </View>
        )}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  headerContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 56,
  },
  headerText: {
    fontFamily: primitive.fontFamily.$headers,
    fontSize: 20,
    letterSpacing: -0.42,
  },
  iconContainer: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
